import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import OnboardingTour from "@/components/OnboardingTour";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProjectsAPI, ReferralsAPI, apiErrorMessage } from "@/lib/api";import { useToast } from "@/hooks/use-toast";
import {
  Flame, Layers, Wand2, Eraser, Camera, KeyRound, ArrowRight, Zap, Crown, Clock,
  FolderOpen, Trash2, Download, Share2, Copy, Users, Gift, Check,
} from "lucide-react";

const TOOLS = [
  { icon: Layers, name: "Halftone Studio", to: "/studio/halftone", desc: "12 styles · batch · CMYK sim process · mockup" },
  { icon: Wand2, name: "AI Vector Creator", to: "/studio/vector", desc: "Prompt or upload → clean vector graphics" },
  { icon: Eraser, name: "Watermark Remover", to: "/studio/watermark", desc: "1-click stock watermark cleanup" },
  { icon: Camera, name: "Photo Upgrader", to: "/studio/upgrade", desc: "Award-winning photographer retouch" },
];

function planLabel(plan) {
  return ({
    free: "Free",
    pro_token: "Pro (Token)",
    creator_token: "Creator (Token)",
    halftone_lifetime: "Halftone Lifetime",
    pro_lifetime: "All-Access Lifetime",
  })[plan] || plan;
}

function ProjectsSection() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const { toast } = useToast();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await ProjectsAPI.list();
      setItems(r.data);
    } catch (err) {
      toast({ title: "Couldn't load projects", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  React.useEffect(() => { load(); }, [load]);

  const download = async (p) => {
    try {
      // List view omits export_base64 for perf; fetch full record on demand.
      const full = p.export_base64 ? p : (await ProjectsAPI.get(p.id)).data;
      if (!full.export_base64) return;
      const a = document.createElement("a");
      a.href = `data:image/png;base64,${full.export_base64}`;
      a.download = `${(full.name || "project").replace(/\s+/g, "_")}.png`;
      a.click();
    } catch (err) {
      toast({ title: "Download failed", description: apiErrorMessage(err), variant: "destructive" });
    }
  };
  const remove = async (id) => {
    try { await ProjectsAPI.remove(id); setItems((it) => it.filter((x) => x.id !== id)); toast({ title: "Deleted" }); }
    catch (err) { toast({ title: "Delete failed", description: apiErrorMessage(err), variant: "destructive" }); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-5 w-5 text-[#4aa4ff]" />
          <h3 className="display-font text-lg font-bold">Your Projects</h3>
        </div>
        <span className="mono text-[10px] uppercase text-muted-foreground">{items.length} saved</span>
      </div>
      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No saved projects yet. In the Halftone Studio, click <span className="text-foreground">Save to Projects</span> after rendering.
        </div>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((p) => (
            <div key={p.id} className="group rounded-xl border border-border bg-background p-3">
              <div className="aspect-square overflow-hidden rounded-md bg-white">
                {p.thumbnail_base64 ? (
                  <img src={`data:image/png;base64,${p.thumbnail_base64}`} alt={p.name} className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full items-center justify-center text-xs text-muted-foreground">no thumb</div>
                )}
              </div>
              <div className="mt-2 truncate text-sm font-medium">{p.name}</div>
              <div className="mono text-[10px] text-muted-foreground">{p.kind}{p.style ? " · " + p.style : ""}</div>
              <div className="mt-2 flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-1 text-xs" onClick={() => download(p)}><Download className="h-3 w-3" /> PNG</Button>
                <Button variant="ghost" size="sm" className="text-xs text-red-400 hover:text-red-500" onClick={() => remove(p.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReferralSection() {
  const [data, setData] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const { toast } = useToast();

  React.useEffect(() => {
    (async () => {
      try {
        const r = await ReferralsAPI.me();
        setData(r.data);
      } catch (err) {
        toast({ title: "Couldn't load referrals", description: apiErrorMessage(err), variant: "destructive" });
      }
    })();
  }, [toast]);

  const copy = (txt) => { navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="rounded-2xl chrome-border p-6">
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-[#f5c845]" />
        <h3 className="display-font text-lg font-bold">Refer & earn</h3>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">Share your link. When someone signs up and buys a paid plan, we credit both accounts. Email us at hello@furnacedesign.lab to claim.</p>
      {data ? (
        <>
          <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-background p-3">
            <div className="mono flex-1 truncate text-sm text-foreground">{data.share_url}</div>
            <Button variant="outline" size="sm" className="gap-2 text-xs" onClick={() => copy(data.share_url)}>{copied ? <Check className="h-3.5 w-3.5 text-[#4aa4ff]" /> : <Copy className="h-3.5 w-3.5" />} {copied ? "Copied" : "Copy"}</Button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="mono text-[10px] uppercase text-muted-foreground">Sign-ups</div>
              <div className="display-font text-2xl font-bold">{data.signups}</div>
            </div>
            <div className="rounded-lg border border-border bg-background p-3">
              <div className="mono text-[10px] uppercase text-muted-foreground">Paid conversions</div>
              <div className="display-font text-2xl font-bold text-[#4aa4ff]">{data.conversions}</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Share2 className="h-3.5 w-3.5 text-[#4aa4ff]" /> Your code: <span className="mono text-foreground">{data.code}</span>
          </div>
        </>
      ) : (
        <div className="py-6 text-sm text-muted-foreground">Loading…</div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user, usage } = useAuth();
  const proExp = usage?.pro_expires_at ? new Date(usage.pro_expires_at) : null;

  return (
    <div className="grain">
      <Navbar />
      <OnboardingTour />
      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Flame className="h-3 w-3 text-[#4aa4ff]" /> Dashboard</span>
            <h1 className="display-font mt-3 text-4xl font-bold">Welcome back, {user?.name?.split(" ")[0] || "printer"}.</h1>
            <p className="mt-1 text-sm text-muted-foreground">Pick a tool and forge press-ready art in seconds.</p>
          </div>
          <Link to="/pricing"><Button variant="outline" className="gap-2">Upgrade <ArrowRight className="h-4 w-4" /></Button></Link>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Plan</div>
            <div className="mt-1 display-font text-2xl font-bold">{planLabel(usage?.plan || "free")}</div>
            {usage?.pro ? (
              <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#4aa4ff]"><Crown className="h-3.5 w-3.5" /> Pro active</div>
            ) : (
              <Link to="/pricing" className="mt-2 inline-flex items-center gap-1 text-xs text-[#4aa4ff] hover:underline">Go Lifetime →</Link>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Exports this month</div>
            <div className="mt-1 flex items-end gap-2">
              <span className="display-font text-3xl font-bold">{usage?.exports_used_this_month ?? 0}</span>
              <span className="mb-1 text-sm text-muted-foreground">/ {usage?.pro ? "∞" : usage?.limit}</span>
            </div>
            {!usage?.pro && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                <div className="h-full bg-gradient-to-r from-[#4aa4ff] to-[#1e6fff]" style={{ width: `${Math.min(100, ((usage?.exports_used_this_month ?? 0) / (usage?.limit || 1)) * 100)}%` }} />
              </div>
            )}
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Pro window</div>
            {proExp ? (
              <>
                <div className="mt-1 display-font text-2xl font-bold">{proExp.toLocaleDateString()}</div>
                <div className="mt-2 inline-flex items-center gap-1 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> expires {proExp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              </>
            ) : (
              <>
                <div className="mt-1 display-font text-2xl font-bold text-muted-foreground">—</div>
                <Link to="/redeem" className="mt-2 inline-flex items-center gap-1 text-xs text-[#4aa4ff] hover:underline"><KeyRound className="h-3.5 w-3.5" /> Redeem a Pro code</Link>
              </>
            )}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="display-font text-2xl font-bold">Your tools</h2>
          <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {TOOLS.map((t) => (
              <Link to={t.to} key={t.name} className="card-hover rounded-2xl border border-border bg-card p-5">
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-[#4aa4ff]"><t.icon className="h-5 w-5" /></div>
                <div className="display-font text-base font-bold">{t.name}</div>
                <div className="mt-1 text-sm text-muted-foreground">{t.desc}</div>
                <div className="mt-4 inline-flex items-center gap-1 text-sm text-[#4aa4ff]">Open <ArrowRight className="h-3.5 w-3.5" /></div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-10 grid gap-4 lg:grid-cols-[1.6fr_1fr]">
          <ProjectsSection />
          <ReferralSection />
        </div>

        <div className="mt-10 rounded-2xl border border-[#4aa4ff]/40 bg-gradient-to-br from-[#0a1024] to-card p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="chip"><Zap className="h-3 w-3 text-[#4aa4ff]" /> Pro tip</div>
              <h3 className="display-font mt-2 text-xl font-bold">Creator + All-Access get automatic DTF/DTG optimize on every image.</h3>
              <p className="mt-1 text-sm text-muted-foreground">Unlimited exports plus auto print-ready cleanup for 30 days with a Creator token, or forever with the $100 lifetime.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/redeem"><Button variant="outline">Redeem code</Button></Link>
              <Link to="/pricing"><Button className="btn-ember font-semibold">See plans</Button></Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
